import type { ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { CHART } from '@/lib/chartColors';

/** One headline number, with its trend over the period when there is one. */
export function StatTile({ label, value, hint, trend, loading }: {
  label: string; value: ReactNode; hint?: ReactNode; trend?: number[]; loading?: boolean;
}) {
  const id = `trend-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const hasTrend = !!trend && trend.length > 1 && trend.some((n) => n > 0);
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-border bg-surface p-5 ${hasTrend ? 'pb-16' : ''}`}>
      <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
      {loading ? (
        <div aria-hidden="true">
          <div className="skeleton mt-3 h-8 w-28" />
          <div className="skeleton mt-3 h-3 w-36" />
        </div>
      ) : (
        <>
          <p className="mt-2 text-[28px] font-semibold leading-tight tracking-tight tabular-nums text-foreground">{value}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </>
      )}
      {hasTrend && !loading && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 opacity-80" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend!.map((v, i) => ({ i, v }))} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART.primary} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={CHART.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={CHART.primary} strokeWidth={1.5} fill={`url(#${id})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
